import { useCallback, useContext, useEffect, useRef } from 'react';

import { PortalConfigContext } from 'src/context/portalConfigContext';
import { useAppSelector } from 'src/hooks/useStore';
import {
  PostAuditEventInput,
  usePostAuditEventMutation,
} from 'src/services/api/auditLogApi';
import { RootState } from 'src/store';

// --- Constants ---
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const THROTTLE_MS = 1000; // 1 second
const LEADER_HEARTBEAT_MS = 5000; // 5 seconds
const LEADER_STALE_MS = 10000; // 10 seconds
const DEBOUNCE_MS = 5000; // 5 seconds between events

const LEADER_KEY_PREFIX = 'assembly_session_leader_';
export const SESSION_STATE_KEY_PREFIX = 'assembly_session_state_';

const ACTIVITY_EVENTS: Array<[string, AddEventListenerOptions?]> = [
  ['mousedown'],
  ['keydown'],
  ['scroll', { passive: true }],
  ['touchstart'],
];

// --- Types ---
export type SessionState = 'idle' | 'active';

export type EmittedEventType = 'sessionStarted' | 'sessionEnded';

export interface PersistedState {
  state: SessionState;
  lastActivityAt: number;
  sessionStartedAt: number;
  // Shared across tabs so the 5s debounce between emissions holds across a
  // leader handoff (the per-tab ref alone can't coordinate that). The debounce
  // is scoped to same-type emits — a sessionEnded followed by a sessionStarted
  // (the normal end-of-session → new-session boundary) passes through.
  lastEventEmittedAt?: number;
  lastEventType?: EmittedEventType;
}

interface LeaderEntry {
  tabId: string;
  timestamp: number;
}

export const parsePersisted = (raw: string | null): PersistedState | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
};

// Resets persisted state to idle and emits the sessionEnded audit event for
// the supplied snapshot values. Single source of truth for the close-and-emit
// payload, used by both the inactivity timer (tracker) and explicit logout.
// Caller is responsible for committing to the snapshot — this function does
// NOT re-read localStorage so it can't drift between decision and write.
// Resets state BEFORE the network call so a mid-flight unload still leaves
// localStorage in a state that won't trigger orphan-backfill on next login.
export const closeSessionAndEmitEnded = async (
  key: string,
  lastActivityAt: number,
  sessionStartedAt: number,
  postAuditEvent: (input: PostAuditEventInput) => Promise<unknown> | unknown,
): Promise<void> => {
  const sessionDurationMs = Math.max(0, lastActivityAt - sessionStartedAt);

  try {
    const next: PersistedState = {
      state: 'idle',
      lastActivityAt,
      sessionStartedAt: 0,
      lastEventEmittedAt: Date.now(),
      lastEventType: 'sessionEnded',
    };
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable — proceed with the emit anyway
  }

  try {
    await postAuditEvent({
      eventType: 'sessionEnded',
      eventDescription: 'Session ended',
      context: { sessionDurationMs },
      lastActivityAt: new Date(lastActivityAt).toISOString(),
    });
  } catch {
    // fire-and-forget — audit failures must not block the caller
  }
};

export const useActivitySessionTracker = () => {
  const portalConfig = useContext(PortalConfigContext);
  const actorId = useAppSelector((state: RootState) => state.user.id);
  const [postAuditEvent] = usePostAuditEventMutation();

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaderIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabIdRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now()}`,
  );
  const workspaceRef = useRef<{ workspaceId: string; workspaceName: string }>({
    workspaceId: '',
    workspaceName: '',
  });
  const lastThrottledActivityRef = useRef<number>(0);
  const mountedRef = useRef(true);

  const postAuditEventRef = useRef(postAuditEvent);
  postAuditEventRef.current = postAuditEvent;

  const portalId = portalConfig.portalHeader;
  // Keys are scoped per (portalId, actorId) so that User A's localStorage
  // record can't leak into User B's session if they share a browser.
  const sessionStateKey = `${SESSION_STATE_KEY_PREFIX}${portalId}_${actorId}`;
  const leaderKey = `${LEADER_KEY_PREFIX}${portalId}_${actorId}`;

  // --- Shared state helpers (localStorage — shared across tabs in this browser) ---
  const readPersistedState = useCallback((): PersistedState | null => {
    try {
      return parsePersisted(localStorage.getItem(sessionStateKey));
    } catch {
      return null;
    }
  }, [sessionStateKey]);

  const writePersistedState = useCallback(
    (state: SessionState, lastActivityAt: number, sessionStartedAt: number) => {
      try {
        const existing = parsePersisted(localStorage.getItem(sessionStateKey));
        const data: PersistedState = {
          state,
          lastActivityAt,
          sessionStartedAt,
          lastEventEmittedAt: existing?.lastEventEmittedAt,
          lastEventType: existing?.lastEventType,
        };
        localStorage.setItem(sessionStateKey, JSON.stringify(data));
      } catch {
        // localStorage may be unavailable (private mode, quota, etc.) — tracker no-ops
      }
    },
    [sessionStateKey],
  );

  // --- Leadership (who fires the inactivity timer) ---
  const isLeader = useCallback((): boolean => {
    try {
      const raw = localStorage.getItem(leaderKey);
      if (!raw) return false;
      const entry = JSON.parse(raw) as LeaderEntry;
      return entry.tabId === tabIdRef.current;
    } catch {
      return false;
    }
  }, [leaderKey]);

  const claimLeadership = useCallback(() => {
    try {
      const entry: LeaderEntry = {
        tabId: tabIdRef.current,
        timestamp: Date.now(),
      };
      localStorage.setItem(leaderKey, JSON.stringify(entry));
    } catch {
      // ignore
    }
  }, [leaderKey]);

  const releaseLeadership = useCallback(() => {
    try {
      const raw = localStorage.getItem(leaderKey);
      if (!raw) return;
      const entry = JSON.parse(raw) as LeaderEntry;
      if (entry.tabId === tabIdRef.current) {
        localStorage.removeItem(leaderKey);
      }
    } catch {
      // ignore
    }
  }, [leaderKey]);

  // Debounce is tracked in the shared localStorage record (not a per-tab ref),
  // so the 5s between-emissions guard holds across a leader handoff — if tab A
  // emits and hands leadership to tab B within the window, tab B sees the
  // shared timestamp and declines to re-emit. Cross-type transitions (ended →
  // started, the normal end-of-session boundary) are always allowed; the
  // debounce only guards against same-type duplicates.
  const canEmitEvent = useCallback(
    (type: EmittedEventType): boolean => {
      const s = readPersistedState();
      if (!s) return true;
      if (s.lastEventType !== type) return true;
      const last = s.lastEventEmittedAt ?? 0;
      return Date.now() - last >= DEBOUNCE_MS;
    },
    [readPersistedState],
  );

  const markEventEmitted = useCallback(
    (type: EmittedEventType) => {
      const s = readPersistedState();
      if (!s) return;
      try {
        const data: PersistedState = {
          ...s,
          lastEventEmittedAt: Date.now(),
          lastEventType: type,
        };
        localStorage.setItem(sessionStateKey, JSON.stringify(data));
      } catch {
        // ignore
      }
    },
    [readPersistedState, sessionStateKey],
  );

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // --- Event emission (leader-only) ---
  const emitSessionStarted = useCallback(
    (previousLastActivityAt?: number) => {
      if (!isLeader() || !canEmitEvent('sessionStarted')) return;
      markEventEmitted('sessionStarted');
      void postAuditEventRef.current({
        eventType: 'sessionStarted',
        eventDescription: 'Session started',
        context: {},
        ...(previousLastActivityAt
          ? { lastActivityAt: new Date(previousLastActivityAt).toISOString() }
          : {}),
      });
    },
    [isLeader, canEmitEvent, markEventEmitted],
  );

  const emitSessionEnded = useCallback(
    (lastActivityAt: number, sessionStartedAt: number) => {
      if (!isLeader() || !canEmitEvent('sessionEnded')) return;
      void closeSessionAndEmitEnded(
        sessionStateKey,
        lastActivityAt,
        sessionStartedAt,
        postAuditEventRef.current,
      );
    },
    [isLeader, canEmitEvent, sessionStateKey],
  );

  // Ref lets the timer callback reschedule via the latest function on re-fire,
  // avoiding a stale closure if dependencies (emitSessionEnded etc.) are
  // recreated between scheduling and firing.
  const scheduleInactivityCheckRef = useRef<() => void>(() => {});
  const scheduleInactivityCheck = useCallback(() => {
    clearInactivityTimer();
    if (!isLeader()) return;
    const s = readPersistedState();
    if (!s || s.state !== 'active') return;

    const elapsed = Date.now() - s.lastActivityAt;
    const remaining = Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed);

    inactivityTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      const current = readPersistedState();
      if (!current || current.state !== 'active') return;
      const idleFor = Date.now() - current.lastActivityAt;
      if (idleFor >= INACTIVITY_TIMEOUT_MS) {
        emitSessionEnded(current.lastActivityAt, current.sessionStartedAt);
      } else {
        // Activity happened in another tab that advanced lastActivityAt — reschedule.
        scheduleInactivityCheckRef.current();
      }
    }, remaining);
  }, [clearInactivityTimer, isLeader, readPersistedState, emitSessionEnded]);
  scheduleInactivityCheckRef.current = scheduleInactivityCheck;

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledActivityRef.current < THROTTLE_MS) return;
    lastThrottledActivityRef.current = now;

    const prev = readPersistedState();
    const wasActive =
      prev !== null && prev.state === 'active' && prev.lastActivityAt > 0;
    // Orphan = prior state was 'active' but lastActivityAt is older than the
    // inactivity window (user returned after the prior session timed out without
    // being closed). Its lastActivityAt travels on the new sessionStarted so the
    // backend backfill can stamp the synthetic sessionEnded accurately.
    const isOrphan =
      prev !== null &&
      prev.state === 'active' &&
      prev.lastActivityAt > 0 &&
      now - prev.lastActivityAt >= INACTIVITY_TIMEOUT_MS;
    const isTransition = !wasActive || isOrphan;
    const sessionStartedAt = isTransition
      ? now
      : (prev?.sessionStartedAt ?? now);

    writePersistedState('active', now, sessionStartedAt);

    // Leader emits directly for its own activity (storage events don't fire in the
    // originating tab). Followers rely on the leader's storage listener.
    if (isTransition && isLeader()) {
      emitSessionStarted(isOrphan ? prev!.lastActivityAt : undefined);
    }
    scheduleInactivityCheck();
  }, [
    readPersistedState,
    writePersistedState,
    isLeader,
    emitSessionStarted,
    scheduleInactivityCheck,
  ]);

  // --- Workspace info refs ---
  useEffect(() => {
    workspaceRef.current = {
      workspaceId: portalConfig.portalHeader,
      workspaceName: portalConfig.name,
    };
  }, [portalConfig.portalHeader, portalConfig.name]);

  // --- Main effect: leader election, activity listeners, visibility ---
  useEffect(() => {
    // Don't run the tracker until we know who the actor is. Auth state resolves
    // after the component first mounts; running with an empty actorId would
    // write to a `{portalId}_` key and then migrate once actorId resolves,
    // leaking a transient "empty actor" record.
    if (!actorId) return;
    mountedRef.current = true;

    const tryClaimLeadership = () => {
      try {
        const raw = localStorage.getItem(leaderKey);
        if (!raw) {
          claimLeadership();
          return;
        }
        const entry = JSON.parse(raw) as LeaderEntry;
        if (Date.now() - entry.timestamp > LEADER_STALE_MS) {
          claimLeadership();
        }
      } catch {
        claimLeadership();
      }
    };

    tryClaimLeadership();

    leaderIntervalRef.current = setInterval(() => {
      if (isLeader()) {
        claimLeadership();
        scheduleInactivityCheck();
      } else {
        tryClaimLeadership();
      }
    }, LEADER_HEARTBEAT_MS);

    // Storage events fire on OTHER tabs when this tab's localStorage writes
    // propagate. The leader observes follower activity this way.
    const handleStorage = (e: StorageEvent) => {
      if (e.key === leaderKey) {
        tryClaimLeadership();
        return;
      }
      if (e.key !== sessionStateKey) return;
      if (!isLeader()) return;

      const newVal = parsePersisted(e.newValue);
      const oldVal = parsePersisted(e.oldValue);
      const wasActive =
        oldVal !== null &&
        oldVal.state === 'active' &&
        oldVal.lastActivityAt > 0;
      const nowActive =
        newVal !== null &&
        newVal.state === 'active' &&
        newVal.lastActivityAt > 0;
      const isTransition =
        nowActive &&
        (!wasActive || oldVal!.sessionStartedAt !== newVal!.sessionStartedAt);

      if (isTransition) {
        const isOrphan =
          wasActive &&
          oldVal!.lastActivityAt > 0 &&
          newVal!.lastActivityAt - oldVal!.lastActivityAt >=
            INACTIVITY_TIMEOUT_MS;
        emitSessionStarted(isOrphan ? oldVal!.lastActivityAt : undefined);
      }
      scheduleInactivityCheck();
    };
    window.addEventListener('storage', handleStorage);

    // Arm the timer against whatever state is already persisted. If the prior
    // session was active and its inactivity window already expired (e.g. browser
    // closed or crashed before sessionEnded fired), the timer will fire
    // immediately and emit sessionEnded(lastActivityAt).
    scheduleInactivityCheck();

    const activityHandler = () => handleActivity();
    ACTIVITY_EVENTS.forEach(([event, options]) => {
      document.addEventListener(event, activityHandler, options);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleInactivityCheck();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearInactivityTimer();
      if (leaderIntervalRef.current !== null) {
        clearInterval(leaderIntervalRef.current);
        leaderIntervalRef.current = null;
      }
      ACTIVITY_EVENTS.forEach(([event, options]) => {
        document.removeEventListener(event, activityHandler, options);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      // Release the old portal's leader key on workspace switch so another
      // tab of that portal can claim immediately without waiting for
      // staleness. Note: React cleanup does NOT run on tab close; that case
      // is handled by the LEADER_STALE_MS takeover in surviving tabs.
      releaseLeadership();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId, actorId]);
};
